import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ApiTask, Task, TaskStatus } from '@/types/tasks.ts';
import { useHouseApiTasks } from './useHouseApiTasks.ts';
import { mapApiStatusToTaskStatus } from '@/utils/taskStatusUtils.ts';
import { isTaskActive } from '@/utils/taskUtils.ts';

/**
 * Hook that synchronizes local tasks with server tasks
 * Updates task statuses and handles completion callbacks
 */
export const useTaskSync = (
	houseId: number | null,
	tasks: Task[],
	updateTask: (taskId: string, updates: Partial<Task>) => void,
	removeTask: (id: string) => void,
) => {
	const tasksRef = useRef<Task[]>(tasks);
	const updateTaskRef = useRef(updateTask);
	const removeTaskRef = useRef(removeTask);
	const activeHouseIdRef = useRef<number | null>(houseId);

	useEffect(() => {
		tasksRef.current = tasks;
		updateTaskRef.current = updateTask;
		removeTaskRef.current = removeTask;
	}, [tasks, updateTask, removeTask]);

	useEffect(() => {
		activeHouseIdRef.current = houseId;
	}, [houseId]);

	const hasActivePollingTasks = useMemo(() => {
		const activeTasks = tasks.filter((task) => task.isPollingTask && isTaskActive(task));

		const houseActiveTasks = activeTasks.filter((task) => {
			// More flexible houseId matching - check both metadata.houseId and direct comparison
			const taskHouseId = task.metadata?.houseId || (task.metadata as Record<string, unknown>)?.house_id;
			return taskHouseId === houseId;
		});

		return houseActiveTasks.length > 0;
	}, [tasks, houseId]);

	const { data: apiTasks = [], isLoading } = useHouseApiTasks(houseId, { shouldPoll: hasActivePollingTasks }) as {
		data: ApiTask[];
		isLoading: boolean;
	};
	const callbacksRef = useRef<Record<string, (task: Task) => Promise<void>>>({});
	const beforeCompleteCallbacksRef = useRef<Record<string, (task: Task) => Promise<void>>>({});

	const storeCallbacks = useCallback(
		(taskId: string, callback?: (task: Task) => Promise<void>, beforeCallback?: (task: Task) => Promise<void>) => {
			if (callback) {
				callbacksRef.current[taskId] = callback;
			}
			if (beforeCallback) {
				beforeCompleteCallbacksRef.current[taskId] = beforeCallback;
			}
		},
		[],
	);

	const taskUpdates = useMemo(() => {
		if (isLoading || !apiTasks.length) return [];

		// Create a map of API tasks by task_id for quick lookup
		const apiTaskMap = new Map<string, ApiTask>();
		apiTasks.forEach((apiTask) => {
			apiTaskMap.set(apiTask.task_id, apiTask);
		});

		// Process local tasks that need status updates - use ref to avoid circular dependency
		const localTasksToUpdate = tasksRef.current.filter((task) => task.isPollingTask && apiTaskMap.has(task.id));

		return localTasksToUpdate
			.map((localTask) => {
				const apiTask = apiTaskMap.get(localTask.id)!;
				const newStatus = mapApiStatusToTaskStatus(apiTask.status);
				const newProgress = apiTask.progress;

				// Only return update if status or progress changed
				if (localTask.status !== newStatus || localTask.progress !== newProgress) {
					return {
						localTask,
						apiTask,
						newStatus,
						newProgress,
					};
				}
				return null;
			})
			.filter(Boolean);
	}, [apiTasks, isLoading]);

	const handleTaskCompletion = useCallback(async (localTask: Task, newStatus: TaskStatus, newProgress: number) => {
		if (newStatus === TaskStatus.Completed) {
			// Trigger before complete callback
			const beforeCallback = beforeCompleteCallbacksRef.current[localTask.id];
			if (beforeCallback) {
				try {
					await beforeCallback(localTask);
				} catch (error) {
					console.error(`Before complete callback failed for task ${localTask.id}:`, error);
				}
				delete beforeCompleteCallbacksRef.current[localTask.id];
			}
		}

		// Update task status
		updateTaskRef.current(localTask.id, {
			status: newStatus,
			progress: newProgress,
		});

		// Schedule cleanup with house guard
		const cleanupDelay = newStatus === TaskStatus.Completed ? 120_000 : 300_000; // 2min for completed, 5min for failed
		const scheduledForHouseId = activeHouseIdRef.current;
		setTimeout(() => {
			// If house changed since scheduling, skip cleanup to avoid cross-house removals
			if (activeHouseIdRef.current !== scheduledForHouseId) return;
			removeTaskRef.current(localTask.id);
		}, cleanupDelay);

		// Trigger completion callback
		const callback = callbacksRef.current[localTask.id];
		if (callback) {
			try {
				await callback({ ...localTask, status: newStatus, progress: newProgress });
			} catch (error) {
				console.error(`Completion callback failed for task ${localTask.id}:`, error);
			}
			delete callbacksRef.current[localTask.id];
		}
	}, []);

	const handleProgressUpdate = useCallback((taskId: string, newProgress: number) => {
		updateTaskRef.current(taskId, { progress: newProgress });
	}, []);

	const processTaskUpdate = useCallback(
		async (
			update: {
				localTask: Task;
				apiTask: ApiTask;
				newStatus: TaskStatus;
				newProgress: number;
			} | null,
		) => {
			if (!update) return;

			const { localTask, newStatus, newProgress } = update;

			// Handle completion or failure
			if (newStatus === TaskStatus.Completed || newStatus === TaskStatus.Failed) {
				await handleTaskCompletion(localTask, newStatus, newProgress);
			} else {
				// Just update progress for in-progress tasks
				handleProgressUpdate(localTask.id, newProgress);
			}
		},
		[handleTaskCompletion, handleProgressUpdate],
	);

	useEffect(() => {
		taskUpdates.forEach(processTaskUpdate);
	}, [taskUpdates, processTaskUpdate]);

	return {
		apiTasks,
		isLoading,
		storeCallbacks,
	};
};
